import React, { useState, useRef } from 'react';
import apiService from '../../services/apiService';

export default function ImportLocalisationsModal({ isOpen, onClose, onImport }) {
  const [importMethod, setImportMethod] = useState('photo');
  const [importText, setImportText] = useState('');
  const [multiLocaux, setMultiLocaux] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [aiError, setAiError] = useState('');
  const [aiStep, setAiStep] = useState('');
  const [selectedFileName, setSelectedFileName] = useState('');

  const cameraInputRef = useRef(null);
  const fileInputRef = useRef(null);

  if (!isOpen) return null;

  const reset = () => {
    setAiError('');
    setAiStep('');
    setSelectedFileName('');
    setIsProcessing(false);
  };

  const handleManualImport = () => {
    if (!importText.trim()) {
      alert('Veuillez saisir des données à importer');
      return;
    }

    const lines = importText.split('\n').filter(line => line.trim());
    const equipements = lines.map((line, index) => {
      const parts = line.split(/[,;\t]/).map(p => p.trim());
      return {
        type: 'equipement_importe',
        designation: parts[0] || `Équipement ${index + 1}`,
        quantite: parts[1] || '1',
        description: parts[2] || '',
        obs_ids: []
      };
    });

    const importedData = {
      multi_locaux: false,
      local: { nom: 'Local importé', ie: '' },
      equipements: equipements
    };

    onImport(importedData);
    onClose();
  };

  const openCamera = () => {
    if (isProcessing) return;
    reset();
    if (cameraInputRef.current) {
      cameraInputRef.current.value = '';
      cameraInputRef.current.click();
    }
  };

  const openFileChooser = () => {
    if (isProcessing) return;
    reset();
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
      fileInputRef.current.click();
    }
  };

  const processFile = async (file) => {
    if (!file) return;
    console.log('[ImportLocalisationsModal] processFile', { name: file.name, type: file.type, size: file.size });
    setSelectedFileName(file.name || 'photo');
    setIsProcessing(true);
    setAiError('');
    setAiStep("Envoi du fichier…");

    try {
      const upload = await apiService.uploadFile(file, 'document');
      console.log('[ImportLocalisationsModal] upload result', upload);
      if (!upload?.success || !upload?.file_url) {
        throw new Error(upload?.error || 'Erreur upload fichier');
      }

      setAiStep("Analyse IA du fichier…");
      const analysis = await apiService.analyzeLocalisationsImage(upload.file_url);
      console.log('[ImportLocalisationsModal] analysis result', analysis);
      if (!analysis?.success) {
        throw new Error(analysis?.error || 'Erreur analyse localisations');
      }

      const data = analysis?.data || {};
      const payload = data?.multi_locaux
        ? { multi_locaux: true, locaux: data.locaux || [] }
        : { local: data.local || { nom: '', ie: '' }, equipements: data.equipements || [] };

      onImport(payload);
      onClose();
    } catch (error) {
      console.error('[ImportLocalisationsModal] error', error);
      setAiError(error?.message || 'Erreur lors du traitement du fichier');
    } finally {
      setIsProcessing(false);
      setAiStep('');
    }
  };

  const handleCameraChange = async (e) => {
    const file = e.target.files?.[0];
    if (file) await processFile(file);
  };

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (file) await processFile(file);
  };

  return (
    <>
      <input
        ref={cameraInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        onChange={handleCameraChange}
        style={{ position: 'absolute', left: '-9999px', width: '1px', height: '1px', opacity: 0 }}
        aria-hidden="true"
      />
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*,application/pdf"
        onChange={handleFileChange}
        style={{ position: 'absolute', left: '-9999px', width: '1px', height: '1px', opacity: 0 }}
        aria-hidden="true"
      />

      <div className="modal-overlay" onClick={onClose}>
        <div className="modal-content modal-large" onClick={(e) => e.stopPropagation()}>
          <div className="modal-header">
            <h3 className="modal-title">📋 Importer des localisations</h3>
            <button onClick={onClose} className="modal-close">✕</button>
          </div>

          <div className="modal-body">
            <div className="import-method-selector">
              <button
                onClick={() => { reset(); setImportMethod('photo'); }}
                className={`method-button ${importMethod === 'photo' ? 'active' : ''}`}
              >
                📷 Photo
              </button>
              <button
                onClick={() => { reset(); setImportMethod('fichier'); }}
                className={`method-button ${importMethod === 'fichier' ? 'active' : ''}`}
              >
                📄 Fichier
              </button>
              <button
                onClick={() => { reset(); setImportMethod('manual'); }}
                className={`method-button ${importMethod === 'manual' ? 'active' : ''}`}
              >
                ⌨️ Saisie
              </button>
            </div>

            {importMethod === 'photo' && (
              <div className="import-ai-section">
                <p className="form-help" style={{ marginTop: 0, marginBottom: 12 }}>
                  Prends en photo une grille de localisations / équipements. L'IA extrait automatiquement le contenu.
                </p>
                <button
                  type="button"
                  onClick={openCamera}
                  disabled={isProcessing}
                  className="save-button"
                  style={{
                    width: '100%',
                    padding: '16px',
                    fontSize: '16px',
                    fontWeight: 600,
                    background: isProcessing ? '#9ca3af' : 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '12px',
                    cursor: isProcessing ? 'not-allowed' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 8,
                  }}
                >
                  {isProcessing ? '⏳ Traitement…' : '📷 Prendre une photo'}
                </button>

                {selectedFileName && (
                  <div style={{ marginTop: 12, fontSize: 12, color: '#475569' }}>
                    Fichier : <strong>{selectedFileName}</strong>
                  </div>
                )}
              </div>
            )}

            {importMethod === 'fichier' && (
              <div className="import-ai-section">
                <p className="form-help" style={{ marginTop: 0, marginBottom: 12 }}>
                  Sélectionne une image (depuis la galerie) ou un PDF déjà disponible sur l'appareil.
                </p>
                <button
                  type="button"
                  onClick={openFileChooser}
                  disabled={isProcessing}
                  className="save-button"
                  style={{
                    width: '100%',
                    padding: '16px',
                    fontSize: '16px',
                    fontWeight: 600,
                    background: isProcessing ? '#9ca3af' : 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '12px',
                    cursor: isProcessing ? 'not-allowed' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 8,
                  }}
                >
                  {isProcessing ? '⏳ Traitement…' : '📄 Choisir un fichier'}
                </button>

                {selectedFileName && (
                  <div style={{ marginTop: 12, fontSize: 12, color: '#475569' }}>
                    Fichier : <strong>{selectedFileName}</strong>
                  </div>
                )}
              </div>
            )}

            {importMethod === 'manual' && (
              <div className="import-manual-section">
                <div className="form-group">
                  <label className="form-label">
                    Données à importer (une ligne par équipement)
                  </label>
                  <p className="form-help">
                    Format : <code>Désignation, Quantité, Description</code>
                    <br />
                    Exemple : <code>Prise 16A, 5, Prises murales</code>
                  </p>
                  <textarea
                    value={importText}
                    onChange={(e) => setImportText(e.target.value)}
                    placeholder="Prise 16A, 5, Prises murales&#10;Luminaire LED, 10, Éclairage général&#10;Armoire électrique, 1, TGBT"
                    className="form-textarea"
                    rows={10}
                  />
                </div>

                <div className="form-group">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={multiLocaux}
                      onChange={(e) => setMultiLocaux(e.target.checked)}
                    />
                    <span>Importer plusieurs locaux (séparés par ligne vide)</span>
                  </label>
                </div>
              </div>
            )}

            {(importMethod === 'photo' || importMethod === 'fichier') && isProcessing && (
              <div
                style={{
                  marginTop: 16,
                  padding: 12,
                  background: '#eff6ff',
                  border: '1px solid #bfdbfe',
                  borderRadius: 8,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  color: '#1d4ed8',
                  fontSize: 13,
                }}
              >
                <div className="spinner" style={{ width: 18, height: 18 }}></div>
                <div>{aiStep || 'Traitement en cours…'}</div>
              </div>
            )}

            {aiError && (
              <div
                style={{
                  marginTop: 16,
                  padding: 12,
                  background: '#fef2f2',
                  border: '1px solid #fecaca',
                  borderRadius: 8,
                  color: '#b91c1c',
                  fontSize: 13,
                }}
              >
                <strong>⚠️ Erreur</strong>
                <div style={{ marginTop: 4 }}>{aiError}</div>
              </div>
            )}
          </div>

          <div className="modal-footer">
            <button onClick={onClose} className="cancel-button">
              Annuler
            </button>
            {importMethod === 'manual' && (
              <button
                onClick={handleManualImport}
                disabled={!importText.trim()}
                className="save-button"
              >
                Importer
              </button>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
